import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Box, MenuItem, SelectChangeEvent } from "@mui/material";
import { Divider } from "@mui/material";
import images from "../../../../Assets/images";
import VcDatePicker from "Components/UI/DatePicker/DatePicker";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { scrollBar } from "Components/Common/Header/ManageOutsideClickClose";
import "../../../../Assets/css/date-picker.css";
import { useApiCall } from "Hooks/useApiCall";
import { getLocalStorage, setLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import { set } from "date-fns";
import ButtonElement from "../../../UI/Button";
import "./style.css";
import ReactPaginate from "react-paginate";
import { pageOptions } from "StaticData/UserManageMent/status";
import Pagination from "Components/UI/Pagination/Pagination";
import { Padding } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getProjectSettingList } from "Network/Core/Booking/booking";
import { setTransportRoundTrip } from "Store/Actions/ProjectSettings/TransportRoundTripCreator";
import {
  downloadOffRoadReports,
  getMaterialTransportReports,
  getOffRoadReports,
} from "Network/Core/EquipmentMaster/equipment";
import { convertDistance, extractNumber, formatDate, toFormattedDateString } from "Library/Utils";

const MaterialEmissionReport: React.FC = () => {
  const { t } = useTranslation();
  const { call } = useApiCall();
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const inputRefS = useRef<HTMLDivElement>(null);
  const inputRefE = useRef<HTMLDivElement>(null);
  const [orderBy, setOrderBy] = useState<string>("date");
  const [statusIsASC, setStatusIsASC] = useState<boolean>(true);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [equipmentRegistryList, setEquipmentRegistryList] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [pageCount, setPageCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
  const countryUom = getLocalStorage(ls_vars.COUNTRY_UOM);
  const [startDate, setStartDate] = useState<any>("");
  const [endDate, setEndDate] = useState<any>("");
  const [companyOptions, setCompanyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [contactNamesOptions, setContactNamesOptions] = useState<
    { label: string; value: string }[]
  >([]);
  // Add new state variables for filtered data and selected values
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const dispatch = useDispatch();
  const isTransportRoundTrip = useSelector(
    (state: any) => state?.TransportRoundTripReducer?.isTransportRoundTrip
  );
  const [reportData, setReportData] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const headerStyle = {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      setSearchValue(searchTerm);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value.trim());
  };

  // Debounce utility function
  function debounce(func: Function, wait: number) {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  useEffect(() => {
    getProjectSetting();
  }, []);

  const getProjectSetting = async () => {
    call(
      () => getProjectSettingList(projectRef),
      (data) => {
        setLocalStorage(ls_vars.PROJECT_SETTINGS, JSON.stringify(data.results));
        const TransportRoundTrip = data?.results?.find(
          (item: { setting: number }) => item.setting === 12
        )?.value;
        dispatch(setTransportRoundTrip(TransportRoundTrip));
      }
    );
  };

  useEffect(() => {
    const fetchReportData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const formattedStartDate = startDate
          ? moment(startDate).format("YYYY-MM-DD")
          : "";
        const formattedEndDate = endDate
          ? moment(endDate).format("YYYY-MM-DD")
          : "";

        call(
          () =>
            getMaterialTransportReports(
              {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                search: searchValue || undefined,
              },
              region,
              project.ref,
              page,
              perPage,
              orderBy
            ),
          (response: any) => {
            setReportData(response.results || response.data || response);
            setTotalCount(response.count || response.total || 0);
            setPageCount(Math.ceil((response.count || response.total || 0) / perPage));
          }
        );
      } catch (err) {
        setError("Failed to fetch equipment usage report");
        console.error("Error fetching equipment usage report:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (startDate && endDate && region) {
      fetchReportData();
    }
  }, [startDate, endDate, region, page, perPage, searchValue, orderBy]);

  scrollBar(setIsScrolling);
  const toggleSort = (field: string, isAcs: boolean) => {
    if (field !== "id") {
      setStatusIsASC(!isAcs);
      setOrderBy(isAcs ? field : "-" + field);
    }
  };

  const handleDateChange = (date: Date | null, type: "start" | "end") => {
    if (type === "start") {
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const exportReportData = async (reportType: string) => {
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";

    call(
      () =>
        downloadOffRoadReports(
          {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            reportType: reportType,
            search: searchValue || undefined,
            orderBy: orderBy || undefined
          },
          region,
          project.ref
        ),
      (response: any) => { }
    );
  };

  useEffect(() => {
    let today = new Date();
    let endDate = new Date();
    // Get one month after date
    endDate.setMonth(today.getMonth() + 1);
    endDate.setHours(0, 0, 0, 0);

    setStartDate(formatDate(today));
    setEndDate(formatDate(endDate));
  }, []);

  // Reset page to 1 when date filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);
  
  return (
    <div className="page-wrapper">
      <div className="page-header">
        <h3 className="page-title">
          {t("Translation.vehicle_emission_reports")}
        </h3>
      </div>
      <div className="page-content custom-list-div ">
        <div style={styles.fixedSection}>
          {/* Date Range and Search */}
          <div className="booking-list-form">
            <Box
              sx={{
                padding: "0 24px 22px 24px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              {/* Single row with date filters, search, and download button */}
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                  flexWrap: "wrap",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                {/* Left side with date pickers */}
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "nowrap",
                  flex: "0 1 auto",
                }}>
                  <Box className="date-box-frontend">
                    <VcDatePicker
                      date={startDate}
                      onDateChange={(val: Date | null) =>
                        handleDateChange(val, "start")
                      }
                    />
                  </Box>
                  <Box className="to" sx={{ marginBottom: 0 }}>
                    {t("Translation.to")}
                  </Box>
                  <Box className="date-box-frontend">
                    <VcDatePicker
                      date={endDate}
                      onDateChange={(val) => handleDateChange(val, "end")}
                      minDate={new Date(startDate || new Date())}
                    />
                  </Box>
                </Box>

                {/* Middle - search box */}
                <Box sx={{
                  flex: "1 1 auto",
                  minWidth: "200px",
                }}>
                  <div className="form-group form-inline filter-search ml-0 mr-0" style={{ marginBottom: 0 }}>
                    <img
                      src={images.search}
                      alt="search"
                      style={{
                        position: "relative",
                        left: "30px",
                        zIndex: 1,
                        height: "20px"
                      }}
                    />
                    <input
                      type="text"
                      className="form-control"
                      style={{
                        paddingLeft: "40px",
                        background: "#eff3f7",
                        borderRadius: "6px",
                        height: "40px",
                        marginBottom: "0px"
                      }}
                      placeholder={t("Translation.search_by_vendor_material_vehicle_type")}
                      value={searchInput}
                      onChange={handleSearchChange}
                    />
                  </div>
                </Box>

                {/* Right side - download button */}
                <Box sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  flex: "0 0 auto",
                }}>
                  <ButtonElement
                    buttonLabel={<img src={images.download} alt="Download" />}
                    handleClick={() => exportReportData("mt")}
                    className="btn white-btn dl-btn"
                  />
                </Box>
              </Box>
            </Box>
          </div>

          <Divider />
        </div>

        <div className="booking-list-wrapper">
          <div
            className={`booking-list-data scroll ${isScrolling ? "show-scroll" : "no-scroll"
              }`}
             style={{ flex: "1 1 0%", overflow: "auto", padding: "0px 24px 24px" }}
          >
            <div className="table-responsive " style={{ overflowX: "auto" }}>
              <table className="display nowrap dataTable w-100">
                <thead>
                  <tr>
                    <th
                      className="sorting sorting_asc"
                      onClick={() => toggleSort("vendor", statusIsASC)}
                    >
                      <span>{t("Translation.vendor")}</span>
                    </th>
                    <th
                      className="sorting"
                      onClick={() => toggleSort("date", statusIsASC)}
                    >
                      <span>{t("Translation.date")}</span>
                    </th>
                    <th
                      className="sorting"
                      onClick={() => toggleSort("material", statusIsASC)}
                    >
                      <span>{t("Translation.material")}</span>
                    </th>
                    <th
                      className="sorting"
                      onClick={() => toggleSort("typeOfVehicle", statusIsASC)}
                    >
                      <span>{t("Translation.type_of_vehicle")}</span>
                    </th>
                    <th
                      className="sorting"
                      onClick={() =>
                        toggleSort("vehicleMpg", statusIsASC)
                      }
                    >
                      <span>{t(countryUom?.distance?.name === "KM" ? "Translation.vehicle_kmpl" : "Translation.vehicle_mpg")}</span>
                    </th>
                    <th
                      className="sorting"
                      onClick={() =>
                        toggleSort("oneWayTripDistance", statusIsASC)
                      }
                    >
                      <span>{t("Translation.one_way_trip_distance")  + ` (${countryUom?.distance?.name?.toLocaleLowerCase()})`}</span>
                    </th>
                    <th
                      className="sorting"
                      onClick={() =>
                        toggleSort(
                          "oneWayTripVehicleCoefficient",
                          statusIsASC
                        )
                      }
                    >
                      <span>
                        {t("Translation.one_way_trip_vehicle_coefficient")}
                      </span>
                    </th>
                    <th
                      className="sorting"
                      onClick={() =>
                        toggleSort(
                          "oneWayTripCO2_emissions",
                          statusIsASC
                        )
                      }
                    >
                      <span>{t("Translation.one_way_trip_co2_emissions")}</span>
                    </th>
                    {isTransportRoundTrip && (
                      <th
                        className="sorting"
                        onClick={() =>
                          toggleSort(
                            "roundtripDistanceDeliveredFromJobsite",
                            statusIsASC
                          )
                        }
                      >
                        <span>
                          {t("Translation.roundtrip_distance_delivered_from_jobsite" ) + ` (${countryUom?.distance?.name?.toLocaleLowerCase()})`}
                        </span>
                      </th>
                    )}

                    {isTransportRoundTrip && (
                      <th
                        className="sorting"
                        onClick={() =>
                          toggleSort(
                            "roundTripVehicleCoefficient",
                            statusIsASC
                          )
                        }
                      >
                        <span>
                          {t("Translation.round_trip_vehicle_coefficient")}
                        </span>
                      </th>
                    )}
                    {isTransportRoundTrip && (
                      <th
                        className="sorting"
                        onClick={() =>
                          toggleSort(
                            "roundTripCO2_emissions",
                            statusIsASC
                          )
                        }
                      >
                        <span>{t("Translation.round_trip_co2_emissions")}</span>
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index} className="odd ">
                      <td>{row.vendor}</td>
                      <td>{row.date && toFormattedDateString(row.date)}</td>
                      <td>{row.materialName}</td>
                      <td>{row.typeOfVehicle}</td>
                      <td>{row.vehicleMpg}</td>
                      <td>{row.oneWayTripDistance || "-"}</td>
                      <td>{row.oneWayTripVehicleCoefficient}</td>
                      <td>{row.oneWayTripCO2_emissions}</td>
                      {isTransportRoundTrip && (
                        <td>{row.roundtripDistanceDeliveredFromJobsite || "-"}</td>
                      )}
                      {isTransportRoundTrip && (
                        <td>{row.roundTripVehicleCoefficient}</td>
                      )}
                      {isTransportRoundTrip && (
                        <td>{row.roundTripCO2_emissions}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalCount > 0 ? (
              <Pagination
                page={page}
                onPageChange={(v) => setPage(v)}
                perPage={perPage}
                onPerPageChange={({ value }) => {
                  setPerPage(value);
                  setPage(1);
                }}
                pageTotalCount={pageCount}
              />
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialEmissionReport;
const styles = {
  headerStyle: {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
  },
  filterContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
    gap: "14px",
  },
  selectStyle: {
    width: "100%",
    height: "48px",
    background: "#EFF3F6",
    color: "#A0AEBD",
    border: "none",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  dateRangeContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "14px",
  },
  datePickerWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  lastColumnWrapper: {
    display: "flex",
    justifyContent: "flex-end", // or 'flex-end' if you only want the button
    alignItems: "center",
  },
  downloadBtn: {
    border: "1px solid rgb(216 217 218)",
    borderRadius: "8px",
    cursor: "pointer",
  },
  dateRangeSeparator: {
    marginLeft: "10px",
    marginRight: "10px",
  },
  dateBoxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },
  searchImage: {
    // top: "14px" as "14px",
    position: "relative" as "relative",
    height: "20px",
    width: "20px",
    left: "30px" as "30px",
  },
  searchBox: {
    height: "45px" as "45px",
    width: "70%" as "70%",
    paddingLeft: "25px" as "25px",
    background: "#EFF3F6" as "#EEF3F6",
    border: "none" as "none",
  },
  tableHeader: {
    backgroundColor: "#EFF3F6",
    color: "#5A5A5A",
    "& th": {
      padding: "12px",
    },
  },
  tableRow: {
    "& td": {
      padding: "12px",
    },
  },
  fixedSection: {
    backgroundColor: "#fff",
    zIndex: 1,
    flexShrink: 0, // Prevents section from shrinking
  }
};
